<script setup>
import { ref, computed, onMounted, watch } from 'vue';
import { useDebounceFn } from '@vueuse/core';
import { useStore } from 'vuex';
import { useRouter, useRoute } from 'vue-router';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useAccount } from 'dashboard/composables/useAccount';
import { FEATURE_FLAGS } from 'dashboard/featureFlags';
import SingleSelect from 'dashboard/components-next/filter/inputs/SingleSelect.vue';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import BreadcrumbTitle from 'dashboard/components-next/breadcrumb/BreadcrumbTitle.vue';
import GalleryImagePicker from 'dashboard/routes/dashboard/settings/inbox/components/GalleryImagePicker.vue';
import SettingsLayout from '../SettingsLayout.vue';
import LoadingIndicator from 'shared/components/LoadingIndicator.vue';
import CustomAttribute from 'dashboard/components/CustomAttribute.vue';
import { isDefaultEventAttributeType } from 'dashboard/routes/dashboard/planning/events/eventAttributeDefaults';
import { useSuppliersStore } from 'dashboard/stores/suppliers';

const store = useStore();
const router = useRouter();
const route = useRoute();
const { t } = useI18n();
const suppliersStore = useSuppliersStore();

const loading = ref(true);
const initialized = ref(false);
const saveStatus = ref('idle'); // 'idle' | 'saving' | 'saved'
const pricingPlanId = ref(null);

const { currentAccount, isCloudFeatureEnabled } = useAccount();
const suppliersEnabled = computed(() =>
  isCloudFeatureEnabled(FEATURE_FLAGS.SUPPLIERS)
);
const planningResourcesEnabled = computed(() =>
  isCloudFeatureEnabled(FEATURE_FLAGS.PLANNING_RESOURCES)
);
const currentUser = computed(() => store.getters.getCurrentUser);
const isSuperAdmin = computed(() => currentUser.value?.type === 'SuperAdmin');
const pricingPlanRoute = computed(() => {
  if (!isEditing.value || !isSuperAdmin.value || !pricingPlanId.value)
    return null;
  return {
    name: 'settings_pricing_plan_show',
    params: {
      accountId: currentAccount.value?.id,
      planId: pricingPlanId.value,
    },
  };
});
const accountCurrency = computed(
  () =>
    currentAccount.value?.settings?.default_currency ||
    currentAccount.value?.default_currency ||
    'EUR'
);

const startingFromPrice = computed(() => {
  if (formData.value.pricing_mode !== 'dynamic') return null;

  const defaultPrice = pi =>
    pi?.prices?.find(p => p.is_default) || pi?.prices?.[0];

  const qtyForUnit = unit => {
    if (unit === 'per_guest') return formData.value.min_guests || 1;
    if (unit === 'per_hour') return formData.value.min_hours || 1;
    if (unit === 'per_hour_per_guest')
      return (formData.value.min_guests || 1) * (formData.value.min_hours || 1);
    return 1;
  };

  const spaceTotal = (formData.value.applicable_space_item_ids || []).reduce(
    (sum, piId) => {
      const pi = pricingItems.value.find(i => i.id === piId);
      const price = defaultPrice(pi);
      if (!price) return sum;
      const qty = qtyForUnit(price.unit);
      return sum + (parseFloat(price.price) || 0) * qty;
    },
    0
  );

  const includedItems = [
    ...(formData.value.experience_items || []),
    ...(planningResourcesEnabled.value
      ? formData.value.resource_items || []
      : []),
  ];
  const itemTotal = includedItems.reduce((sum, ei) => {
    const pi = pricingItems.value.find(i => i.id === ei.pricing_item_id);
    const price = defaultPrice(pi);
    if (!price) return sum;
    return sum + (parseFloat(price.price) || 0) * (ei.default_quantity || 1);
  }, 0);

  const total = spaceTotal + itemTotal;
  return total > 0 ? total : null;
});

const formattedStartingFromPrice = computed(() => {
  if (startingFromPrice.value == null) return null;
  return new Intl.NumberFormat(undefined, {
    style: 'currency',
    currency: accountCurrency.value,
    maximumFractionDigits: 0,
  }).format(startingFromPrice.value);
});

const formData = ref({
  name: '',
  description: '',
  default_title: '',
  default_event_manager_id: null,
  cover_image_url: '',
  pricing_mode: 'dynamic',
  fixed_price_amount: null,
  hide_price: false,
  fixed_guests: null,
  fixed_hours: null,
  min_guests: null,
  max_guests: null,
  min_hours: null,
  max_hours: null,
  applicable_space_item_ids: [],
  experience_items: [],
  upgrade_items: [],
  resource_items: [],
  require_resource_availability: false,
  default_custom_attributes: {},
  supplier_ids: [],
});

const defaultAttributeKeys = ref(new Set());
const showDefaultAttributePicker = ref(false);

const selectedManager = ref(null);
const availableSuppliers = computed(
  () => suppliersStore.getSuppliersList || []
);

const experienceId = computed(() => route.params.experienceId);
const isEditing = computed(() => !!experienceId.value);

const agents = computed(() => store.getters['agents/getAgents'] || []);
const spaces = computed(() => store.getters['spaces/getSpaces'] || []);
const pricingItems = computed(
  () => store.getters['pricingItems/getRecords'] || []
);
const eventAttributes = computed(
  () =>
    store.getters['attributes/getAttributesByModel']?.('event_attribute') || []
);
const validAttributeKeys = computed(
  () => new Set(eventAttributes.value.map(a => a.attribute_key))
);

const experienceName = computed(
  () => formData.value.name || t('PLANNING_EXPERIENCES.ADD.TITLE')
);

const isFixedPricing = computed(() => formData.value.pricing_mode === 'fixed');
const isDynamicPricing = computed(
  () => formData.value.pricing_mode === 'dynamic'
);

const breadcrumbItems = computed(() => [
  {
    label: t('PLANNING_EXPERIENCES.HEADER'),
    routeName: 'planning_experiences_index',
  },
  {
    label: isEditing.value
      ? experienceName.value
      : t('PLANNING_EXPERIENCES.ADD.TITLE'),
  },
]);

const handleBreadcrumbClick = item => {
  if (item.routeName) {
    router.push({ name: item.routeName });
  }
};

const agentOptions = computed(() => {
  return agents.value.map(agent => ({
    id: agent.id,
    name: agent.name,
  }));
});

const formattedAgents = computed(() =>
  agents.value.map(agent => ({
    id: agent.id,
    name: agent.name,
    email: agent.email,
    thumbnail: agent.thumbnail,
    availability_status: agent.availability_status,
  }))
);

const activeDefaultAttributes = computed(() =>
  eventAttributes.value.filter(
    attr =>
      defaultAttributeKeys.value.has(attr.attribute_key) &&
      isDefaultEventAttributeType(attr)
  )
);

const availableDefaultAttributes = computed(() =>
  eventAttributes.value.filter(
    attr =>
      !defaultAttributeKeys.value.has(attr.attribute_key) &&
      isDefaultEventAttributeType(attr)
  )
);

const updateDefaultAttribute = (key, value) => {
  formData.value.default_custom_attributes = {
    ...(formData.value.default_custom_attributes || {}),
    [key]: value,
  };
};

const deleteDefaultAttribute = key => {
  const next = { ...(formData.value.default_custom_attributes || {}) };
  delete next[key];
  formData.value.default_custom_attributes = next;
  const keys = new Set(defaultAttributeKeys.value);
  keys.delete(key);
  defaultAttributeKeys.value = keys;
};

const addDefaultAttribute = attr => {
  defaultAttributeKeys.value = new Set([
    ...defaultAttributeKeys.value,
    attr.attribute_key,
  ]);
  showDefaultAttributePicker.value = false;
};

const spaceOptions = computed(() => {
  const usedIds = formData.value.applicable_space_item_ids;
  const currentExpId = isEditing.value
    ? parseInt(experienceId.value, 10)
    : null;
  return pricingItems.value
    .filter(
      item =>
        item.category === 'space' &&
        !usedIds.includes(item.id) &&
        (!item.experience_id || item.experience_id === currentExpId) &&
        (!item.attribute_key ||
          validAttributeKeys.value.has(item.attribute_key))
    )
    .map(item => ({
      id: item.id,
      name: item.name,
      icon: 'i-lucide-orbit',
      description: getPricingItemUnit(item.id),
    }));
});

const isBookableResourceRate = item => !!item?.bookable_resource_id;

const usedPricingItemIds = computed(() => [
  ...formData.value.experience_items.map(i => i.pricing_item_id),
  ...formData.value.upgrade_items.map(i => i.pricing_item_id),
  ...(formData.value.resource_items || []).map(i => i.pricing_item_id),
]);

const mapPricingOption = item => ({
  id: item.id,
  name: item.name,
  icon: isBookableResourceRate(item)
    ? 'i-lucide-package'
    : CATEGORY_ICONS[item.category] || 'i-lucide-hash',
  description: getPricingItemUnit(item.id),
});

const pricingItemOptions = computed(() => {
  return pricingItems.value
    .filter(
      item =>
        item.category !== 'space' &&
        !(planningResourcesEnabled.value && isBookableResourceRate(item)) &&
        !usedPricingItemIds.value.includes(item.id) &&
        !item.experience_id &&
        (!item.attribute_key ||
          validAttributeKeys.value.has(item.attribute_key))
    )
    .map(mapPricingOption);
});

const upgradeItemOptions = computed(() => {
  return pricingItems.value
    .filter(
      item =>
        item.category !== 'space' &&
        !usedPricingItemIds.value.includes(item.id) &&
        !item.experience_id &&
        (!item.attribute_key ||
          validAttributeKeys.value.has(item.attribute_key))
    )
    .map(mapPricingOption);
});

const resourceItemOptions = computed(() => {
  if (!planningResourcesEnabled.value) return [];
  return pricingItems.value
    .filter(
      item =>
        isBookableResourceRate(item) &&
        !usedPricingItemIds.value.includes(item.id) &&
        !item.experience_id
    )
    .map(mapPricingOption);
});

const selectedSpace = computed({
  get() {
    const itemId = formData.value.applicable_space_item_ids[0];
    if (!itemId) return null;
    const item = pricingItems.value.find(i => i.id === itemId);
    return item ? { id: item.id, name: item.name } : null;
  },
  set(val) {
    formData.value.applicable_space_item_ids = val ? [val.id] : [];
  },
});

const getPricingItemName = id => {
  const item = pricingItems.value.find(i => i.id === id);
  return item?.name || t('PLANNING_EXPERIENCES.FORM.PRICING.REMOVED_ITEM');
};

const isPricingItemRemoved = id => {
  return !pricingItems.value.find(i => i.id === id);
};

const UNIT_LABELS = {
  per_quantity: 'Quantity',
  per_guest: 'Guest',
  per_hour: 'Hour',
  per_hour_per_guest: 'Hour · Guest',
  per_event: 'Event',
  per_day: 'Day',
};

const getDefaultPriceForItem = id => {
  const item = pricingItems.value.find(i => i.id === id);
  if (!item?.prices?.length) return null;
  return item.prices.find(p => p.is_default) || item.prices[0];
};

const getPricingItemUnit = id => {
  const price = getDefaultPriceForItem(id);
  if (!price) return '';
  const unitLabel = UNIT_LABELS[price.unit] || '';
  return `\u20AC${price.price} / ${unitLabel}`;
};

const isAutoQuantityItem = id => {
  const unit = getDefaultPriceForItem(id)?.unit;
  return unit === 'per_event' || unit === 'per_hour_per_guest';
};

const getItemUnit = id => {
  return getDefaultPriceForItem(id)?.unit || null;
};

const getItemUnitLabel = id => {
  const unit = getItemUnit(id);
  return UNIT_LABELS[unit]?.toLowerCase() || '';
};

// Fixed pricing: derive locked quantity from fixed_hours/fixed_guests
const getFixedQuantityForItem = id => {
  const unit = getItemUnit(id);
  if (unit === 'per_hour') return formData.value.fixed_hours || 1;
  if (unit === 'per_guest') return formData.value.fixed_guests || 1;
  if (unit === 'per_hour_per_guest')
    return (
      (formData.value.fixed_guests || 1) * (formData.value.fixed_hours || 1)
    );
  if (unit === 'per_day')
    return Math.max(1, Math.ceil((formData.value.fixed_hours || 1) / 24));
  return null;
};

// Dynamic pricing: min/max quantity bounds per unit
const getDynMin = id => {
  const unit = getItemUnit(id);
  if (unit === 'per_guest') return formData.value.min_guests || 1;
  if (unit === 'per_hour') return formData.value.min_hours || 1;
  if (unit === 'per_hour_per_guest')
    return (formData.value.min_guests || 1) * (formData.value.min_hours || 1);
  if (unit === 'per_day')
    return Math.max(1, Math.ceil((formData.value.min_hours || 1) / 24));
  return 1;
};

const getDynMax = id => {
  const unit = getItemUnit(id);
  if (unit === 'per_guest' && formData.value.max_guests)
    return formData.value.max_guests;
  if (unit === 'per_hour' && formData.value.max_hours)
    return formData.value.max_hours;
  if (
    unit === 'per_hour_per_guest' &&
    formData.value.max_guests &&
    formData.value.max_hours
  )
    return formData.value.max_guests * formData.value.max_hours;
  if (unit === 'per_day' && formData.value.max_hours)
    return Math.max(1, Math.ceil(formData.value.max_hours / 24));
  return undefined;
};

const CATEGORY_ICONS = {
  space: 'i-lucide-orbit',
  staff: 'i-lucide-user',
  catering: 'i-lucide-utensils',
  av: 'i-lucide-monitor',
  add_on: 'i-lucide-puzzle',
  other: 'i-lucide-code',
};

const getPricingItemIcon = id => {
  const item = pricingItems.value.find(i => i.id === id);
  if (!item) return 'i-lucide-hash';
  if (item.bookable_resource_id) return 'i-lucide-package';
  return CATEGORY_ICONS[item.category] || 'i-lucide-hash';
};

const loadExperience = async id => {
  initialized.value = false;
  loading.value = true;
  try {
    const promises = [];
    if (!agents.value.length) promises.push(store.dispatch('agents/get'));
    if (!spaces.value.length) promises.push(store.dispatch('spaces/get'));
    if (!pricingItems.value.length)
      promises.push(store.dispatch('pricingItems/get'));
    if (!eventAttributes.value.length)
      promises.push(store.dispatch('attributes/get'));
    await Promise.all(promises);

    if (id) {
      const experiences = store.getters['planningExperiences/getRecords'] || [];
      let experience = experiences.find(e => e.id === parseInt(id, 10));

      if (!experience) {
        await store.dispatch('planningExperiences/get');
        const updatedExperiences =
          store.getters['planningExperiences/getRecords'] || [];
        experience = updatedExperiences.find(e => e.id === parseInt(id, 10));
      }

      if (experience) {
        formData.value = {
          name: experience.name || '',
          description: experience.description || '',
          default_title: experience.default_title || '',
          default_event_manager_id: experience.default_event_manager_id || null,
          cover_image_url: experience.cover_image_url || '',
          pricing_mode: experience.pricing_mode || 'dynamic',
          fixed_price_amount: experience.fixed_price_amount || null,
          hide_price: experience.hide_price || false,
          fixed_guests: experience.fixed_guests || null,
          fixed_hours: experience.fixed_hours || null,
          min_guests: experience.min_guests || null,
          max_guests: experience.max_guests || null,
          min_hours: experience.min_hours || null,
          max_hours: experience.max_hours || null,
          applicable_space_item_ids: experience.applicable_space_item_ids || [],
          experience_items: experience.experience_items || [],
          upgrade_items: experience.upgrade_items || [],
          resource_items: experience.resource_items || [],
          require_resource_availability:
            experience.require_resource_availability || false,
          default_custom_attributes: experience.default_custom_attributes || {},
          slot_interval_minutes: experience.slot_interval_minutes || 60,
          allow_unavailable_booking:
            experience.allow_unavailable_booking || false,
          supplier_ids: experience.supplier_ids || [],
        };
        pricingPlanId.value = experience.pricing_plan_id ?? null;
        defaultAttributeKeys.value = new Set(
          Object.keys(experience.default_custom_attributes || {}).filter(
            key => {
              const attr = eventAttributes.value.find(
                a => a.attribute_key === key
              );
              return attr && isDefaultEventAttributeType(attr);
            }
          )
        );
        selectedManager.value = null;
        if (experience.default_event_manager_id) {
          const manager = agents.value.find(
            a => a.id === experience.default_event_manager_id
          );
          selectedManager.value = manager
            ? { id: manager.id, name: manager.name }
            : null;
        }
      }
    }
  } finally {
    loading.value = false;
    initialized.value = true;
  }
};

const toggleSupplier = supplierId => {
  const id = Number(supplierId);
  const ids = [...(formData.value.supplier_ids || [])];
  const index = ids.indexOf(id);
  if (index >= 0) ids.splice(index, 1);
  else ids.push(id);
  formData.value.supplier_ids = ids;
};

onMounted(async () => {
  if (suppliersEnabled.value && !availableSuppliers.value.length) {
    try {
      await suppliersStore.get({ page: 1, sort: 'name' });
    } catch {
      // Suppliers list is optional for loading the experience form.
    }
  }
  await loadExperience(experienceId.value);
});

watch(experienceId, newId => {
  if (newId) loadExperience(newId);
});

const MIN_SAVING_DISPLAY_MS = 800;

const autoSave = async () => {
  if (!isEditing.value || !initialized.value) return;
  if (guestRangeError.value) return;
  if (hourRangeError.value) return;
  saveStatus.value = 'saving';
  const saveStartTime = Date.now();
  try {
    const payload = {
      ...formData.value,
      default_event_manager_id: selectedManager.value?.id || null,
    };
    await store.dispatch('planningExperiences/update', {
      id: parseInt(experienceId.value, 10),
      ...payload,
    });
    const elapsed = Date.now() - saveStartTime;
    if (elapsed < MIN_SAVING_DISPLAY_MS) {
      await new Promise(resolve =>
        setTimeout(resolve, MIN_SAVING_DISPLAY_MS - elapsed)
      );
    }
    saveStatus.value = 'saved';
  } catch (error) {
    saveStatus.value = 'idle';
    const apiMessage = error?.response?.data?.message;
    useAlert(
      apiMessage || t('PLANNING_EXPERIENCES.EDIT.API.ERROR_MESSAGE')
    );
  }
};

const debouncedSave = useDebounceFn(autoSave, 800);

watch(
  [formData, selectedManager],
  () => {
    if (!initialized.value) return;
    saveStatus.value = 'idle';
    debouncedSave();
  },
  { deep: true }
);

const togglePricingMode = value => {
  if (value) {
    formData.value.pricing_mode = 'fixed';
    formData.value.min_guests = null;
    formData.value.max_guests = null;
  } else {
    formData.value.pricing_mode = 'dynamic';
    formData.value.fixed_price_amount = null;
    formData.value.fixed_guests = null;
    formData.value.fixed_hours = null;
  }
};

const addIncludedItem = item => {
  if (!item) return;
  const defaultQty = isFixedPricing.value
    ? (getFixedQuantityForItem(item.id) ?? null)
    : null;
  formData.value.experience_items.push({
    pricing_item_id: item.id,
    default_quantity: defaultQty,
  });
};

const removeIncludedItem = index => {
  formData.value.experience_items.splice(index, 1);
};

const addUpgradeItem = item => {
  if (!item) return;
  formData.value.upgrade_items.push({
    pricing_item_id: item.id,
    default_quantity: null,
  });
};

const removeUpgradeItem = index => {
  formData.value.upgrade_items.splice(index, 1);
};

const addResourceItem = item => {
  if (!item) return;
  formData.value.resource_items.push({
    pricing_item_id: item.id,
    default_quantity: 1,
  });
};

const removeResourceItem = index => {
  formData.value.resource_items.splice(index, 1);
};

const removeSpace = index => {
  formData.value.applicable_space_item_ids.splice(index, 1);
};

const addSpace = space => {
  if (!space) return;
  if (!formData.value.applicable_space_item_ids.includes(space.id)) {
    formData.value.applicable_space_item_ids.push(space.id);
  }
};

const addItemSelector = ref(null);
const addUpgradeSelector = ref(null);

const clampToMin = (value, min) => {
  if (value === null || value === undefined || value === '') return value;
  return value < min ? min : value;
};

const guestRangeError = computed(() => {
  const min = formData.value.min_guests;
  const max = formData.value.max_guests;
  if (min != null && max != null && max < min) {
    return t('PLANNING_EXPERIENCES.FORM.PRICING.MAX_GUESTS_ERROR');
  }
  return null;
});

const hourRangeError = computed(() => {
  const min = formData.value.min_hours;
  const max = formData.value.max_hours;
  if (min != null && max != null && max < min) {
    return t('PLANNING_EXPERIENCES.FORM.PRICING.MAX_HOURS_ERROR');
  }
  return null;
});
</script>

<template>
  <SettingsLayout :is-loading="loading" :no-records-found="false">
    <template #header>
      <div class="flex items-start justify-between gap-4">
        <BreadcrumbTitle
          :items="breadcrumbItems"
          :description="$t('PLANNING_EXPERIENCES.FORM.SUBTITLE')"
          @click="handleBreadcrumbClick"
        />
        <router-link
          v-if="pricingPlanRoute"
          :to="pricingPlanRoute"
          target="_blank"
          class="shrink-0 flex items-center gap-1.5 text-xs text-n-slate-10 hover:text-n-slate-12 mt-1"
        >
          <Icon icon="i-lucide-receipt" class="size-3.5" />
          {{ $t('PLANNING_EXPERIENCES.FORM.PRICING_PLAN_LINK') }}
        </router-link>
      </div>
      <LoadingIndicator
        v-if="saveStatus === 'saving'"
        position="fixed"
        placement="bottom-right"
        variant="primary"
        size="md"
        :message="$t('PLANNING_EXPERIENCES.FORM.SAVING')"
      />
    </template>

    <template #body>
      <form class="max-w-4xl space-y-8 mb-8" @submit.prevent>
        <!-- Overview Section -->
        <div
          class="rounded-lg border border-n-weak bg-white dark:bg-slate-900 p-6"
        >
          <h3 class="text-lg font-semibold text-n-slate-12 mb-4">
            {{ $t('PLANNING.EVENTS.OVERVIEW') }}
          </h3>

          <div class="space-y-6">
            <!-- Name -->
            <div>
              <label class="block text-sm font-medium text-n-slate-12 mb-2">
                {{ $t('PLANNING_EXPERIENCES.FORM.NAME.LABEL') }}
                <span
                  class="text-red-500"
                  :aria-label="$t('PLANNING_EXPERIENCES.FORM.REQUIRED')"
                  >*</span>
              </label>
              <input
                v-model="formData.name"
                type="text"
                required
                class="w-full dark:!bg-neutral-950"
                :placeholder="$t('PLANNING_EXPERIENCES.FORM.NAME.PLACEHOLDER')"
              />
              <p class="mt-1 text-xs text-n-slate-10">
                {{ $t('PLANNING_EXPERIENCES.FORM.NAME.HELP') }}
              </p>
            </div>

            <!-- Description -->
            <div>
              <label class="block text-sm font-medium text-n-slate-12 mb-2">
                {{ $t('PLANNING_EXPERIENCES.FORM.DESCRIPTION.LABEL') }}
              </label>
              <textarea
                v-model="formData.description"
                rows="4"
                class="w-full dark:!bg-neutral-950"
                :placeholder="
                  $t('PLANNING_EXPERIENCES.FORM.DESCRIPTION.PLACEHOLDER')
                "
              />
            </div>

            <!-- Cover Image -->
            <div>
              <GalleryImagePicker
                v-model="formData.cover_image_url"
                :label="$t('PLANNING_EXPERIENCES.FORM.COVER_IMAGE.LABEL')"
                :description="
                  $t('PLANNING_EXPERIENCES.FORM.COVER_IMAGE.DESCRIPTION')
                "
              />
            </div>

            <!-- Default Title -->
            <div>
              <label class="block text-sm font-medium text-n-slate-12 mb-2">
                {{ $t('PLANNING_EXPERIENCES.FORM.DEFAULT_TITLE.LABEL') }}
              </label>
              <input
                v-model="formData.default_title"
                name="default_title"
                type="text"
                class="w-full dark:!bg-neutral-950"
                :placeholder="
                  $t('PLANNING_EXPERIENCES.FORM.DEFAULT_TITLE.PLACEHOLDER')
                "
              />
              <p class="mt-2 text-xs text-n-slate-10">
                {{ $t('PLANNING_EXPERIENCES.FORM.DEFAULT_TITLE.HELP') }}
              </p>
            </div>

            <!-- Default Manager -->
            <div>
              <label class="block text-sm font-medium text-n-slate-12 mb-2">
                {{ $t('PLANNING_EXPERIENCES.FORM.DEFAULT_MANAGER.LABEL') }}
              </label>
              <SingleSelect
                v-model="selectedManager"
                :options="agentOptions"
                :placeholder="
                  $t('PLANNING_EXPERIENCES.FORM.DEFAULT_MANAGER.PLACEHOLDER')
                "
              />
              <p class="mt-1 text-xs text-n-slate-10">
                {{ $t('PLANNING_EXPERIENCES.FORM.DEFAULT_MANAGER.HELP') }}
              </p>
            </div>

            <!-- Default Suppliers -->
            <div v-if="suppliersEnabled">
              <label class="block text-sm font-medium text-n-slate-12 mb-2">
                {{ $t('PLANNING_EXPERIENCES.FORM.SUPPLIERS.LABEL') }}
              </label>
              <p class="mb-3 text-xs text-n-slate-10">
                {{ $t('PLANNING_EXPERIENCES.FORM.SUPPLIERS.HELP') }}
              </p>
              <div
                v-if="availableSuppliers.length"
                class="flex max-h-48 flex-col gap-2 overflow-y-auto rounded-lg border border-n-weak p-3"
              >
                <label
                  v-for="supplier in availableSuppliers"
                  :key="supplier.id"
                  class="flex items-center gap-2 text-sm text-n-slate-12"
                >
                  <input
                    type="checkbox"
                    :checked="formData.supplier_ids.includes(supplier.id)"
                    @change="toggleSupplier(supplier.id)"
                  />
                  {{ supplier.name }}
                </label>
              </div>
              <p v-else class="text-sm text-n-slate-10">
                {{ $t('PLANNING_EXPERIENCES.FORM.SUPPLIERS.EMPTY') }}
              </p>
            </div>

            <!-- Default Event Attributes -->
            <div>
              <label class="block text-sm font-medium text-n-slate-12 mb-2">
                {{ $t('PLANNING_EXPERIENCES.FORM.DEFAULT_ATTRIBUTES.TITLE') }}
              </label>
              <p class="mb-3 text-xs text-n-slate-10">
                {{ $t('PLANNING_EXPERIENCES.FORM.DEFAULT_ATTRIBUTES.HELP') }}
              </p>
              <div v-if="activeDefaultAttributes.length" class="space-y-1">
                <CustomAttribute
                  v-for="attr in activeDefaultAttributes"
                  :key="attr.id"
                  :label="attr.attribute_display_name"
                  :description="attr.attribute_description"
                  :values="attr.attribute_values || []"
                  :value="
                    formData.default_custom_attributes?.[attr.attribute_key] ??
                    ''
                  "
                  :show-actions="true"
                  :attribute-type="attr.attribute_display_type"
                  :attribute-regex="attr.regex_pattern"
                  :regex-cue="attr.regex_cue"
                  :attribute-key="attr.attribute_key"
                  :agents="formattedAgents"
                  :account-id="currentAccount?.id"
                  @update="updateDefaultAttribute"
                  @delete="deleteDefaultAttribute"
                />
              </div>
              <p v-else class="text-sm text-n-slate-10 mb-3">
                {{ $t('PLANNING_EXPERIENCES.FORM.DEFAULT_ATTRIBUTES.EMPTY') }}
              </p>
              <div v-if="availableDefaultAttributes.length" class="relative">
                <button
                  type="button"
                  class="inline-flex items-center gap-2 px-3 text-sm font-medium text-n-slate-11 hover:text-woot-600 transition-colors rounded-lg border border-n-weak h-8"
                  @click="
                    showDefaultAttributePicker = !showDefaultAttributePicker
                  "
                >
                  <Icon icon="i-lucide-plus" class="size-4" />
                  {{ $t('PLANNING_EXPERIENCES.FORM.DEFAULT_ATTRIBUTES.ADD') }}
                </button>
                <div
                  v-if="showDefaultAttributePicker"
                  class="absolute top-full left-0 mt-1 w-64 max-h-48 overflow-y-auto bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700 rounded-lg shadow-lg z-50 py-1"
                >
                  <button
                    v-for="attr in availableDefaultAttributes"
                    :key="attr.id"
                    type="button"
                    class="w-full px-4 py-2 text-left text-sm hover:bg-slate-50 dark:hover:bg-slate-700 transition-colors"
                    @click="addDefaultAttribute(attr)"
                  >
                    {{ attr.attribute_display_name }}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        <!-- Pricing Section -->
        <div
          class="rounded-lg border border-n-weak bg-white dark:bg-slate-900 p-6"
        >
          <h3 class="text-lg font-semibold text-n-slate-12 mb-6">
            {{ $t('PLANNING_EXPERIENCES.FORM.PRICING.TITLE') }}
          </h3>

          <!-- Fixed Price Switch -->
          <div class="flex flex-col gap-1.5 mb-6">
            <div class="flex items-center gap-3">
              <Switch
                :model-value="isFixedPricing"
                @update:model-value="togglePricingMode"
              />
              <label class="text-sm font-medium text-n-slate-12">
                {{ $t('PLANNING_EXPERIENCES.FORM.PRICING.FIXED') }}
              </label>
            </div>
            <p class="text-xs text-n-slate-10 ml-10">
              {{ $t('PLANNING_EXPERIENCES.FORM.PRICING.FIXED_SWITCH_HELP') }}
            </p>
          </div>

          <!-- Hide Price Switch -->
          <div class="flex items-center justify-between gap-4 mb-6">
            <div>
              <label class="block text-sm font-medium text-n-slate-12">
                {{ $t('PLANNING_EXPERIENCES.FORM.PRICING.HIDE_PRICE') }}
              </label>
              <p class="mt-1 text-xs text-n-slate-10">
                {{ $t('PLANNING_EXPERIENCES.FORM.PRICING.HIDE_PRICE_HELP') }}
              </p>
            </div>
            <Switch v-model="formData.hide_price" />
          </div>

          <!-- Fixed Pricing Fields -->
          <div v-if="isFixedPricing" class="space-y-6">
            <!-- Fixed Price Amount -->
            <div class="flex flex-col gap-1.5">
              <label class="text-sm font-medium text-n-slate-12">
                {{ $t('PLANNING_EXPERIENCES.FORM.PRICING.FIXED_PRICE') }}
              </label>
              <div class="relative">
                <input
                  v-model.number="formData.fixed_price_amount"
                  type="number"
                  step="0.01"
                  min="0"
                  class="w-full pr-10 dark:!bg-neutral-950"
                  @change="
                    formData.fixed_price_amount = clampToMin(
                      formData.fixed_price_amount,
                      0
                    )
                  "
                />
                <span
                  class="absolute right-3 top-1/2 -translate-y-1/2 text-n-slate-10 text-sm"
                >
                  {{ accountCurrency }}
                </span>
              </div>
              <p class="text-xs text-n-slate-10">
                {{ $t('PLANNING_EXPERIENCES.FORM.PRICING.FIXED_PRICE_HELP') }}
              </p>
            </div>

            <!-- Applicable Space (single) -->
            <div class="flex flex-col gap-1.5">
              <label class="text-sm font-medium text-n-slate-12">
                {{ $t('PLANNING_EXPERIENCES.FORM.PRICING.APPLICABLE_SPACE') }}
              </label>
              <p class="text-xs text-n-slate-10">
                {{
                  $t('PLANNING_EXPERIENCES.FORM.PRICING.APPLICABLE_SPACE_HELP')
                }}
              </p>
              <div
                v-if="selectedSpace"
                class="flex items-center justify-between p-3 rounded-lg border border-n-weak"
              >
                <div class="flex items-center gap-3">
                  <Icon icon="i-lucide-layout" class="size-5 text-n-slate-10" />
                  <span class="text-sm font-medium text-n-slate-12">{{
                    selectedSpace.name
                  }}</span>
                </div>
                <div class="flex items-center gap-4">
                  <span class="text-sm text-n-slate-10">{{
                    getPricingItemUnit(
                      pricingItems.find(i => i.space_id === selectedSpace.id)
                        ?.id
                    )
                  }}</span>
                  <button
                    type="button"
                    class="text-red-400 hover:text-red-600"
                    @click="selectedSpace = null"
                  >
                    <Icon icon="i-lucide-trash-2" class="size-4" />
                  </button>
                </div>
              </div>
              <div
                v-else-if="formData.applicable_space_item_ids.length > 0"
                class="flex items-center justify-between p-3 rounded-lg border border-red-500 bg-red-50 dark:bg-red-950/20"
              >
                <div class="flex items-center gap-3">
                  <Icon icon="i-lucide-layout" class="size-5 text-red-500" />
                  <span
                    class="text-sm font-medium text-red-600 dark:text-red-400"
                  >
                    {{ $t('PLANNING_EXPERIENCES.FORM.PRICING.REMOVED_ITEM') }}
                  </span>
                </div>
                <button
                  type="button"
                  class="text-red-400 hover:text-red-600"
                  @click="formData.applicable_space_item_ids = []"
                >
                  <Icon icon="i-lucide-trash-2" class="size-4" />
                </button>
              </div>
              <SingleSelect
                v-if="
                  !selectedSpace &&
                  formData.applicable_space_item_ids.length === 0
                "
                :options="spaceOptions"
                :placeholder="
                  $t('PLANNING_EXPERIENCES.FORM.PRICING.SELECT_SPACE')
                "
                @update:model-value="
                  val => {
                    selectedSpace = val;
                  }
                "
              />
            </div>

            <!-- Guests & Hours -->
            <div class="grid grid-cols-2 gap-4">
              <div class="flex flex-col gap-1.5">
                <label class="text-sm font-medium text-n-slate-12">
                  {{ $t('PLANNING_EXPERIENCES.FORM.PRICING.GUESTS') }}
                </label>
                <input
                  v-model.number="formData.fixed_guests"
                  type="number"
                  min="1"
                  class="w-full dark:!bg-neutral-950"
                  @change="
                    formData.fixed_guests = clampToMin(formData.fixed_guests, 1)
                  "
                />
                <p class="text-xs text-n-slate-10">
                  {{ $t('PLANNING_EXPERIENCES.FORM.PRICING.GUESTS_HELP') }}
                </p>
              </div>
              <div class="flex flex-col gap-1.5">
                <label class="text-sm font-medium text-n-slate-12">
                  {{ $t('PLANNING_EXPERIENCES.FORM.PRICING.HOURS') }}
                </label>
                <input
                  v-model.number="formData.fixed_hours"
                  type="number"
                  min="1"
                  step="0.5"
                  class="w-full dark:!bg-neutral-950"
                  @change="
                    formData.fixed_hours = clampToMin(formData.fixed_hours, 1)
                  "
                />
                <p class="text-xs text-n-slate-10">
                  {{ $t('PLANNING_EXPERIENCES.FORM.PRICING.HOURS_HELP') }}
                </p>
              </div>
            </div>

            <!-- Standard Included Items -->
            <div class="flex flex-col gap-1.5">
              <label class="text-sm font-medium text-n-slate-12">
                {{ $t('PLANNING_EXPERIENCES.FORM.PRICING.STANDARD_INCLUDED') }}
              </label>
              <p class="text-xs text-n-slate-10">
                {{
                  $t('PLANNING_EXPERIENCES.FORM.PRICING.STANDARD_INCLUDED_HELP')
                }}
              </p>
              <div class="space-y-2">
                <div
                  v-for="(item, index) in formData.experience_items"
                  :key="'included-' + index"
                  class="flex items-center gap-3 p-3 rounded-lg border"
                  :class="[
                    isPricingItemRemoved(item.pricing_item_id)
                      ? 'border-red-500 bg-red-50 dark:bg-red-950/20'
                      : 'border-n-weak',
                  ]"
                >
                  <div class="flex items-center gap-3 flex-1">
                    <Icon
                      :icon="getPricingItemIcon(item.pricing_item_id)"
                      class="size-4 text-n-slate-10"
                    />
                    <span class="text-sm font-medium text-n-slate-12">{{
                      getPricingItemName(item.pricing_item_id)
                    }}</span>
                  </div>
                  <div class="w-16 flex justify-center">
                    <span
                      v-if="
                        getFixedQuantityForItem(item.pricing_item_id) === null
                      "
                      class="w-14 h-8 flex items-center justify-center text-sm text-n-slate-10"
                      >—</span>
                    <template v-else>
                      <input
                        v-model.number="item.default_quantity"
                        type="number"
                        min="1"
                        :max="getFixedQuantityForItem(item.pricing_item_id)"
                        step="1"
                        class="reset-base w-14 h-8 text-center text-sm rounded-lg border border-n-weak dark:!bg-neutral-950"
                        placeholder="—"
                        @change="
                          item.default_quantity = Math.min(
                            getFixedQuantityForItem(item.pricing_item_id),
                            clampToMin(item.default_quantity, 1)
                          )
                        "
                      />
                    </template>
                  </div>
                  <span class="text-sm text-n-slate-10 w-28 text-right">{{
                    getPricingItemUnit(item.pricing_item_id)
                  }}</span>
                  <button
                    type="button"
                    class="text-red-400 hover:text-red-600 shrink-0"
                    @click="removeIncludedItem(index)"
                  >
                    <Icon icon="i-lucide-trash-2" class="size-4" />
                  </button>
                </div>
              </div>
              <div class="flex justify-end">
                <SingleSelect
                  ref="addItemSelector"
                  :options="pricingItemOptions"
                  :placeholder="
                    $t('PLANNING_EXPERIENCES.FORM.PRICING.ADD_ITEM')
                  "
                  class="w-auto"
                  @update:model-value="addIncludedItem"
                />
              </div>
            </div>

            <!-- Upgrades -->
            <div class="flex flex-col gap-1.5">
              <label class="text-sm font-medium text-n-slate-12">
                {{ $t('PLANNING_EXPERIENCES.FORM.PRICING.UPGRADES') }}
              </label>
              <p class="text-xs text-n-slate-10">
                {{ $t('PLANNING_EXPERIENCES.FORM.PRICING.UPGRADES_HELP') }}
              </p>
              <div class="space-y-2">
                <div
                  v-for="(item, index) in formData.upgrade_items"
                  :key="'upgrade-' + index"
                  class="flex items-center justify-between p-3 rounded-lg border"
                  :class="[
                    isPricingItemRemoved(item.pricing_item_id)
                      ? 'border-red-500 bg-red-50 dark:bg-red-950/20'
                      : 'border-n-weak',
                  ]"
                >
                  <div class="flex items-center gap-3">
                    <Icon
                      :icon="getPricingItemIcon(item.pricing_item_id)"
                      class="size-4 text-n-slate-10"
                    />
                    <span class="text-sm font-medium text-n-slate-12">{{
                      getPricingItemName(item.pricing_item_id)
                    }}</span>
                  </div>
                  <div class="flex items-center gap-3">
                    <span class="text-sm text-n-slate-10">{{
                      getPricingItemUnit(item.pricing_item_id)
                    }}</span>
                    <button
                      type="button"
                      class="text-red-400 hover:text-red-600"
                      @click="removeUpgradeItem(index)"
                    >
                      <Icon icon="i-lucide-trash-2" class="size-4" />
                    </button>
                  </div>
                </div>
              </div>
              <div class="flex justify-end">
                <SingleSelect
                  ref="addUpgradeSelector"
                  :options="upgradeItemOptions"
                  :placeholder="
                    $t('PLANNING_EXPERIENCES.FORM.PRICING.ADD_ITEM')
                  "
                  drop-up
                  class="w-auto"
                  @update:model-value="addUpgradeItem"
                />
              </div>
            </div>
          </div>

          <!-- Dynamic / Non-fixed Pricing Fields -->
          <div v-if="isDynamicPricing" class="space-y-6">
            <!-- Starting from price badge -->
            <div
              v-if="formattedStartingFromPrice"
              class="flex items-center gap-2 px-3 py-2 rounded-lg bg-n-violet-3 border border-n-violet-6 w-fit"
            >
              <Icon icon="i-lucide-tag" class="size-4 text-n-violet-11" />
              <span class="text-sm font-medium text-n-violet-11">
                {{
                  $t('PLANNING_EXPERIENCES.FORM.PRICING.STARTING_FROM', {
                    price: formattedStartingFromPrice,
                  })
                }}
              </span>
            </div>
            <!-- Applicable Spaces (multiple) -->
            <div class="flex flex-col gap-1.5">
              <label class="text-sm font-medium text-n-slate-12">
                {{ $t('PLANNING_EXPERIENCES.FORM.PRICING.APPLICABLE_SPACES') }}
              </label>
              <p class="text-xs text-n-slate-10">
                {{
                  $t('PLANNING_EXPERIENCES.FORM.PRICING.APPLICABLE_SPACES_HELP')
                }}
              </p>
              <div class="space-y-2">
                <div
                  v-for="(itemId, index) in formData.applicable_space_item_ids"
                  :key="'space-' + index"
                  class="flex items-center justify-between p-3 rounded-lg border"
                  :class="[
                    isPricingItemRemoved(itemId)
                      ? 'border-red-500 bg-red-50 dark:bg-red-950/20'
                      : 'border-n-weak',
                  ]"
                >
                  <div class="flex items-center gap-3">
                    <Icon
                      icon="i-lucide-orbit"
                      class="size-5 text-n-slate-10"
                    />
                    <span class="text-sm font-medium text-n-slate-12">
                      {{
                        pricingItems.find(i => i.id === itemId)?.name ||
                        `Item #${itemId}`
                      }}
                    </span>
                  </div>
                  <div class="flex items-center gap-4">
                    <span class="text-sm text-n-slate-10">{{
                      getPricingItemUnit(itemId)
                    }}</span>
                    <button
                      type="button"
                      class="text-red-400 hover:text-red-600"
                      @click="removeSpace(index)"
                    >
                      <Icon icon="i-lucide-trash-2" class="size-4" />
                    </button>
                  </div>
                </div>
              </div>
              <SingleSelect
                :options="spaceOptions"
                :placeholder="$t('PLANNING_EXPERIENCES.FORM.PRICING.ADD_SPACE')"
                @update:model-value="addSpace"
              />
            </div>

            <!-- Min / Max Guests -->
            <div class="flex flex-col gap-1.5">
              <div class="grid grid-cols-2 gap-4">
                <div class="flex flex-col gap-1.5">
                  <label class="text-sm font-medium text-n-slate-12">
                    {{ $t('PLANNING_EXPERIENCES.FORM.PRICING.MIN_GUESTS') }}
                  </label>
                  <input
                    v-model.number="formData.min_guests"
                    type="number"
                    min="1"
                    class="w-full dark:!bg-neutral-950"
                    @change="
                      formData.min_guests = clampToMin(formData.min_guests, 1)
                    "
                  />
                </div>
                <div class="flex flex-col gap-1.5">
                  <label class="text-sm font-medium text-n-slate-12">
                    {{ $t('PLANNING_EXPERIENCES.FORM.PRICING.MAX_GUESTS') }}
                  </label>
                  <input
                    v-model.number="formData.max_guests"
                    type="number"
                    min="1"
                    class="w-full dark:!bg-neutral-950"
                    :class="[guestRangeError ? 'border-red-400' : '']"
                    @change="
                      formData.max_guests = clampToMin(formData.max_guests, 1)
                    "
                  />
                </div>
              </div>
              <p v-if="guestRangeError" class="text-xs text-red-500">
                {{ guestRangeError }}
              </p>
            </div>

            <!-- Min / Max Duration (hours) -->
            <div class="flex flex-col gap-1.5">
              <div class="grid grid-cols-2 gap-4">
                <div class="flex flex-col gap-1.5">
                  <label class="text-sm font-medium text-n-slate-12">
                    {{ $t('PLANNING_EXPERIENCES.FORM.PRICING.MIN_HOURS') }}
                  </label>
                  <input
                    v-model.number="formData.min_hours"
                    type="number"
                    min="0.5"
                    step="0.5"
                    class="w-full dark:!bg-neutral-950"
                    @change="
                      formData.min_hours = clampToMin(formData.min_hours, 0.5)
                    "
                  />
                </div>
                <div class="flex flex-col gap-1.5">
                  <label class="text-sm font-medium text-n-slate-12">
                    {{ $t('PLANNING_EXPERIENCES.FORM.PRICING.MAX_HOURS') }}
                  </label>
                  <input
                    v-model.number="formData.max_hours"
                    type="number"
                    min="0.5"
                    step="0.5"
                    class="w-full dark:!bg-neutral-950"
                    :class="[hourRangeError ? 'border-red-400' : '']"
                    @change="
                      formData.max_hours = clampToMin(formData.max_hours, 0.5)
                    "
                  />
                </div>
              </div>
              <p v-if="hourRangeError" class="text-xs text-red-500">
                {{ hourRangeError }}
              </p>
            </div>
            <div class="flex flex-col gap-1.5">
              <label class="text-sm font-medium text-n-slate-12">
                {{ $t('PLANNING_EXPERIENCES.FORM.PRICING.STANDARD_INCLUDED') }}
              </label>
              <p class="text-xs text-n-slate-10">
                {{
                  $t('PLANNING_EXPERIENCES.FORM.PRICING.STANDARD_INCLUDED_HELP')
                }}
              </p>
              <div class="space-y-2">
                <div
                  v-for="(item, index) in formData.experience_items"
                  :key="'dyn-included-' + index"
                  class="flex items-center gap-3 p-3 rounded-lg border"
                  :class="[
                    isPricingItemRemoved(item.pricing_item_id)
                      ? 'border-red-500 bg-red-50 dark:bg-red-950/20'
                      : 'border-n-weak',
                  ]"
                >
                  <div class="flex items-center gap-3 flex-1">
                    <Icon
                      :icon="getPricingItemIcon(item.pricing_item_id)"
                      class="size-4 text-n-slate-10"
                    />
                    <span class="text-sm font-medium text-n-slate-12">{{
                      getPricingItemName(item.pricing_item_id)
                    }}</span>
                  </div>
                  <div class="w-16 flex justify-center">
                    <span
                      v-if="isAutoQuantityItem(item.pricing_item_id)"
                      class="w-14 h-8 flex items-center justify-center text-sm text-n-slate-10"
                      >—</span>
                    <template v-else>
                      <input
                        v-model.number="item.default_quantity"
                        type="number"
                        :min="getDynMin(item.pricing_item_id)"
                        :max="getDynMax(item.pricing_item_id)"
                        step="1"
                        class="reset-base w-14 h-8 text-center text-sm rounded-lg border border-n-weak dark:!bg-neutral-950"
                        placeholder="—"
                        @change="
                          item.default_quantity = Math.min(
                            getDynMax(item.pricing_item_id) ?? Infinity,
                            clampToMin(
                              item.default_quantity,
                              getDynMin(item.pricing_item_id)
                            )
                          )
                        "
                      />
                    </template>
                  </div>
                  <span class="text-sm text-n-slate-10 w-28 text-right">{{
                    getPricingItemUnit(item.pricing_item_id)
                  }}</span>
                  <button
                    type="button"
                    class="text-red-400 hover:text-red-600 shrink-0"
                    @click="removeIncludedItem(index)"
                  >
                    <Icon icon="i-lucide-trash-2" class="size-4" />
                  </button>
                </div>
              </div>
              <div class="flex justify-end">
                <SingleSelect
                  ref="addItemSelector"
                  :options="pricingItemOptions"
                  :placeholder="
                    $t('PLANNING_EXPERIENCES.FORM.PRICING.ADD_ITEM')
                  "
                  drop-up
                  class="w-auto"
                  @update:model-value="addIncludedItem"
                />
              </div>
            </div>

            <!-- Upgrades -->
            <div class="flex flex-col gap-1.5">
              <label class="text-sm font-medium text-n-slate-12">
                {{ $t('PLANNING_EXPERIENCES.FORM.PRICING.UPGRADES') }}
              </label>
              <p class="text-xs text-n-slate-10">
                {{ $t('PLANNING_EXPERIENCES.FORM.PRICING.UPGRADES_HELP') }}
              </p>
              <div class="space-y-2">
                <div
                  v-for="(item, index) in formData.upgrade_items"
                  :key="'dyn-upgrade-' + index"
                  class="flex items-center justify-between p-3 rounded-lg border"
                  :class="[
                    isPricingItemRemoved(item.pricing_item_id)
                      ? 'border-red-500 bg-red-50 dark:bg-red-950/20'
                      : 'border-n-weak',
                  ]"
                >
                  <div class="flex items-center gap-3">
                    <Icon
                      :icon="getPricingItemIcon(item.pricing_item_id)"
                      class="size-4 text-n-slate-10"
                    />
                    <span class="text-sm font-medium text-n-slate-12">{{
                      getPricingItemName(item.pricing_item_id)
                    }}</span>
                    <span
                      class="text-xs text-n-slate-10 bg-n-solid-2 border border-n-weak rounded px-1.5 py-0.5"
                      >{{ getPricingItemUnit(item.pricing_item_id) }}</span>
                  </div>
                  <div class="flex items-center gap-3">
                    <button
                      type="button"
                      class="text-red-400 hover:text-red-600"
                      @click="removeUpgradeItem(index)"
                    >
                      <Icon icon="i-lucide-trash-2" class="size-4" />
                    </button>
                  </div>
                </div>
              </div>
              <div class="flex justify-end">
                <SingleSelect
                  ref="addUpgradeSelector"
                  :options="upgradeItemOptions"
                  :placeholder="
                    $t('PLANNING_EXPERIENCES.FORM.PRICING.ADD_ITEM')
                  "
                  drop-up
                  class="w-auto"
                  @update:model-value="addUpgradeItem"
                />
              </div>
            </div>
          </div>
        </div>

        <!-- Resources Section (planning_resources feature) -->
        <div
          v-if="planningResourcesEnabled"
          class="rounded-lg border border-n-weak bg-white dark:bg-slate-900 p-6"
        >
          <h3 class="text-lg font-semibold text-n-slate-12 mb-4">
            {{ $t('PLANNING_EXPERIENCES.FORM.RESOURCES.TITLE') }}
          </h3>
          <p class="mb-6 text-sm text-n-slate-11">
            {{ $t('PLANNING_EXPERIENCES.FORM.RESOURCES.DESCRIPTION') }}
          </p>

          <div class="space-y-6">
            <div class="flex flex-col gap-1.5">
              <label class="text-sm font-medium text-n-slate-12">
                {{ $t('PLANNING_EXPERIENCES.FORM.RESOURCES.REQUIRED') }}
              </label>
              <p class="text-xs text-n-slate-10">
                {{ $t('PLANNING_EXPERIENCES.FORM.RESOURCES.REQUIRED_HELP') }}
              </p>
              <div class="space-y-2">
                <div
                  v-for="(item, index) in formData.resource_items"
                  :key="'resource-' + index"
                  class="flex items-center gap-3 p-3 rounded-lg border border-n-weak"
                >
                  <div class="flex items-center gap-3 flex-1">
                    <Icon
                      :icon="getPricingItemIcon(item.pricing_item_id)"
                      class="size-4 text-n-slate-10"
                    />
                    <span class="text-sm font-medium text-n-slate-12">{{
                      getPricingItemName(item.pricing_item_id)
                    }}</span>
                  </div>
                  <input
                    v-model.number="item.default_quantity"
                    type="number"
                    min="1"
                    step="1"
                    class="reset-base w-14 h-8 text-center text-sm rounded-lg border border-n-weak dark:!bg-neutral-950"
                    @change="
                      item.default_quantity = clampToMin(
                        item.default_quantity,
                        1
                      )
                    "
                  />
                  <span class="text-sm text-n-slate-10 w-28 text-right">{{
                    getPricingItemUnit(item.pricing_item_id)
                  }}</span>
                  <button
                    type="button"
                    class="text-red-400 hover:text-red-600 shrink-0"
                    @click="removeResourceItem(index)"
                  >
                    <Icon icon="i-lucide-trash-2" class="size-4" />
                  </button>
                </div>
              </div>
              <div class="flex justify-end">
                <SingleSelect
                  :options="resourceItemOptions"
                  :placeholder="
                    $t('PLANNING_EXPERIENCES.FORM.RESOURCES.ADD')
                  "
                  class="w-auto"
                  @update:model-value="addResourceItem"
                />
              </div>
              <p
                v-if="!resourceItemOptions.length && !formData.resource_items.length"
                class="text-xs text-n-slate-10"
              >
                {{ $t('PLANNING_EXPERIENCES.FORM.RESOURCES.EMPTY') }}
              </p>
            </div>

            <div class="flex items-center justify-between gap-4">
              <div>
                <label class="block text-sm font-medium text-n-slate-12">
                  {{
                    $t(
                      'PLANNING_EXPERIENCES.FORM.RESOURCES.REQUIRE_AVAILABILITY'
                    )
                  }}
                </label>
                <p class="mt-1 text-xs text-n-slate-10">
                  {{
                    $t(
                      'PLANNING_EXPERIENCES.FORM.RESOURCES.REQUIRE_AVAILABILITY_HELP'
                    )
                  }}
                </p>
              </div>
              <Switch v-model="formData.require_resource_availability" />
            </div>
          </div>
        </div>

        <!-- Booking Settings Section -->
        <div
          class="rounded-lg border border-n-weak bg-white dark:bg-slate-900 p-6"
        >
          <h3 class="text-lg font-semibold text-n-slate-12 mb-4">
            {{ $t('PLANNING_EXPERIENCES.FORM.BOOKING.TITLE') }}
          </h3>

          <div class="space-y-6">
            <!-- Slot Interval -->
            <div>
              <label class="block text-sm font-medium text-n-slate-12 mb-2">
                {{ $t('PLANNING_EXPERIENCES.FORM.BOOKING.SLOT_INTERVAL') }}
              </label>
              <input
                v-model.number="formData.slot_interval_minutes"
                type="number"
                min="15"
                max="480"
                step="15"
                class="w-32 dark:!bg-neutral-950"
              />
              <p class="mt-1 text-xs text-n-slate-10">
                {{ $t('PLANNING_EXPERIENCES.FORM.BOOKING.SLOT_INTERVAL_HELP') }}
              </p>
            </div>

            <!-- Allow Unavailable Booking -->
            <div class="flex items-center justify-between gap-4">
              <div>
                <label class="block text-sm font-medium text-n-slate-12">
                  {{
                    $t('PLANNING_EXPERIENCES.FORM.BOOKING.ALLOW_UNAVAILABLE')
                  }}
                </label>
                <p class="mt-1 text-xs text-n-slate-10">
                  {{
                    $t(
                      'PLANNING_EXPERIENCES.FORM.BOOKING.ALLOW_UNAVAILABLE_HELP'
                    )
                  }}
                </p>
              </div>
              <Switch v-model="formData.allow_unavailable_booking" />
            </div>
          </div>
        </div>
      </form>
    </template>
  </SettingsLayout>
</template>
